import { Card, Stack } from "@mantine/core";
import { SimpleBreadcrumbs } from "../../../components/SimpleBreadcrumbs";
import { FormattedMessage, useIntl } from "react-intl";
import { useTRPC } from "../../../trpc";
import { TilesetListControls } from "./components/TilesetListControls";
import { useCallback, useMemo, useState } from "react";
import { useDebouncedValue, useLocalStorage } from "@mantine/hooks";
import { TilesetItemCondensed } from "./components/TilesetItemCondensed";
import { TilesetItem } from "./components/TilesetItem";
import { VersionItemControls } from "./components/VersionItemControls";
import { VersionItemTitle } from "./components/VersionItemTitle";
import { useQuery } from "@tanstack/react-query";
import { tilesetDashboardLayoutRoute } from "./routes";
import { usePathAccountName } from "../../../hooks/router";
import type { TilesetVersion } from "./types/tileset";

const sortByDate = <T extends { version: TilesetVersion }>(tilesetWithVersion: T[]): T[] => {
  return tilesetWithVersion.sort(
    (a, b) => new Date(b.version.updatedAt ?? b.version.createdAt).getTime() - new Date(a.version.updatedAt ?? a.version.createdAt).getTime(),
  );
};

export function TilesetVersions() {
  const intl = useIntl();
  const trpc = useTRPC();
  const accountName = usePathAccountName();
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery] = useDebouncedValue(searchQuery, 300);

  const { tileset: tilesetKey } = tilesetDashboardLayoutRoute.useParams();
  const { dataset: datasetKey } = tilesetDashboardLayoutRoute.useSearch();

  const { data: tilesetWithVersions } = useQuery(trpc.getTilesetWithVersions.queryOptions({
    account: accountName,
    tileset: tilesetKey,
    dataset: datasetKey,
  }, {
    refetchInterval: 1000 * 60 * 5,
    refetchOnMount: false,
  }));

  const [isCondensedView, setIsCondensedView] = useLocalStorage({
    key: "tileset-versions-view-mode",
    defaultValue: false,
    getInitialValueInEffect: false,
  });

  const toggleView = useCallback(() => {
    setIsCondensedView(!isCondensedView);
  }, [isCondensedView, setIsCondensedView]);

  const tilesetVersions = useMemo(() => {
    if (!tilesetWithVersions) return [];

    const {
      tileset,
      versions,
      dataset,
      account,
    } = tilesetWithVersions;

    return versions.flatMap(version => ({
      tileset,
      version,
      dataset,
      account,
    }));
  }, [tilesetWithVersions]);

  const filteredVersions = useMemo(() => {
    if (!debouncedQuery.trim()) return sortByDate(tilesetVersions);

    const query = debouncedQuery.toLowerCase().trim();
    const filtered = tilesetVersions.filter((item) => {
      const {
        tileset,
        dataset,
        version: {
          config,
        },
      } = item;

      if (dataset?.key.toLowerCase().includes(query)) return true;
      if (tileset.key.toLowerCase().includes(query)) return true;
      if (config?.description?.toLowerCase().includes(query)) return true;
      if (config?.attribution?.toLowerCase().includes(query)) return true;

      return false;
    });
    return sortByDate(filtered);
  }, [tilesetVersions, debouncedQuery]);

  return (
    <Stack gap={16} px="md" my="md">
      <SimpleBreadcrumbs
        parent={intl.formatMessage({ defaultMessage: "Versions", id: "navigation.versions" })}
        current={datasetKey ? `${datasetKey} - ${tilesetKey}` : tilesetKey}
        description={
          <FormattedMessage id="text.tilesetVersionsDescription" defaultMessage="View your tileset versions." />
        }
      />
      <TilesetListControls
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        isCondensedView={isCondensedView}
        toggleView={toggleView}
      />

      <Stack gap={isCondensedView ? "xs" : "md"}>
        {isCondensedView
          ? (
              filteredVersions.map((tilesetWithVersion) => {
                const {
                  dataset,
                  tileset,
                  version,
                  account,
                } = tilesetWithVersion;

                return (
                  <TilesetItemCondensed
                    key={`${account.name}-${dataset?.key}-${tilesetKey}-${version.id}`}
                    tileset={tileset}
                    account={account}
                    version={version}
                    dataset={dataset}
                    controls={<VersionItemControls tileset={tileset} version={version} />}
                    title={<VersionItemTitle tileset={tileset} dataset={dataset} version={version} />}
                  />
                );
              })
            )
          : (
              filteredVersions.map((tilesetWithVersion) => {
                const {
                  dataset,
                  tileset,
                  version,
                  account,
                } = tilesetWithVersion;

                return (
                  <TilesetItem
                    key={`${dataset?.key}-${tilesetKey}-${version.id}`}
                    tileset={tileset}
                    account={account}
                    version={version}
                    dataset={dataset}
                    info={<></>}
                    controls={<VersionItemControls tileset={tileset} version={version} />}
                    title={<VersionItemTitle tileset={tileset} dataset={dataset} version={version} />}
                  />
                );
              })
            )}

        {filteredVersions.length === 0 && (
          <Card withBorder p="xl" ta="center">
            {searchQuery
              ? (
                  intl.formatMessage({
                    defaultMessage: "No tilesets found matching: {query}",
                    id: "text.noTilesetsFound",
                  }, { query: searchQuery })
                )
              : (
                  intl.formatMessage({
                    defaultMessage: "No tilesets available. Create your first tileset!",
                    id: "text.noTilesets",
                  })
                )}
          </Card>
        )}
      </Stack>
    </Stack>
  );
}
